/**
 * MechanicChecklistRow
 *
 * A mechanic-optimized compact checklist row variant focused on speed:
 * - One-tap completion for checkbox items (tap the row or the checkbox).
 * - Inline quick-note editor that opens in place (no navigation away).
 * - Direct camera/gallery capture through the existing PhotoCapture flow.
 * - Fast inline numeric input with unit display.
 * - Always-visible photo-required indicator (icon + chip), no explanation text.
 * - Touch targets are at least 44px high on mobile.
 *
 * It can be used inline in the mechanic work-order flow and inside the existing
 * ChecklistFillDialog (it shares the same onChange/onAddPhoto contract as
 * ChecklistItemRenderer).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PhotoCapture } from '@/components/inspection/PhotoCapture';
import type {
  ChecklistTemplateItem,
  BookingChecklistEntry,
  ChecklistPhoto,
} from '@/hooks/useChecklists';

/** Minimum touch target height for mobile (WCAG / Apple HIG). */
const MIN_TOUCH_TARGET = 44;

interface Props {
  item: ChecklistTemplateItem;
  entry?: BookingChecklistEntry;
  onChange: (entry: Partial<BookingChecklistEntry> & { itemId: string }) => void;
  disabled?: boolean;
  photos?: ChecklistPhoto[];
  onAddPhoto?: (itemId: string, file: File) => Promise<void>;
  onDeletePhoto?: (itemId: string, photoId: string) => Promise<void>;
  isUploadingPhoto?: boolean;
}

export function MechanicChecklistRow({
  item,
  entry,
  onChange,
  disabled,
  photos,
  onAddPhoto,
  onDeletePhoto,
  isUploadingPhoto,
}: Props) {
  const { t } = useTranslation('checklists');

  const isChecked = entry?.checked ?? false;
  const itemPhotos = photos ?? [];
  const photoRequired = item.photoConfig?.requirePhoto ?? false;
  const photoMissing = photoRequired && itemPhotos.length === 0;
  // A photo-required item must always offer a way to attach a photo, even if
  // `allowPhoto` was not explicitly set, otherwise the mechanic would see a
  // "photo required" warning with no actionable affordance.
  const canAttachPhoto =
    !!onAddPhoto &&
    (item.type === 'photo' || (item.photoConfig?.allowPhoto ?? false) || photoRequired);

  // Reveal an existing note by default so previously-entered text is visible
  // without an extra tap.
  const [noteOpen, setNoteOpen] = useState(() => !!entry?.notes);
  const [photoOpen, setPhotoOpen] = useState(item.type === 'photo' || itemPhotos.length > 0);
  const noteInputRef = useRef<HTMLInputElement>(null);

  // Focus the note field as soon as it is opened so typing is immediate.
  useEffect(() => {
    if (noteOpen) {
      // Defer so the Collapse transition has mounted the input.
      const id = window.setTimeout(() => noteInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [noteOpen]);

  const handleToggleChecked = () => {
    if (disabled) return;
    onChange({ itemId: item.id, checked: !isChecked });
  };

  const handleValueChange = (value: string | number) => {
    onChange({ itemId: item.id, checked: true, value });
  };

  const handleNotesChange = (notes: string) => {
    onChange({ itemId: item.id, checked: isChecked, notes });
  };

  const hasNote = !!(entry?.notes && entry.notes.length > 0);

  // The whole row is tappable for checkbox items (one-tap completion).
  const rowTapToComplete = item.type === 'checkbox' && !disabled;

  return (
    <Box
      data-testid="mechanic-checklist-row"
      sx={{
        borderRadius: 1.5,
        bgcolor: isChecked ? 'success.50' : 'background.paper',
        border: '1px solid',
        borderColor: photoMissing
          ? 'warning.main'
          : isChecked
            ? 'success.200'
            : 'divider',
      }}
    >
      {/* Primary row */}
      <Box
        onClick={rowTapToComplete ? handleToggleChecked : undefined}
        role={rowTapToComplete ? 'button' : undefined}
        aria-pressed={rowTapToComplete ? isChecked : undefined}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1,
          py: 0.5,
          minHeight: MIN_TOUCH_TARGET,
          cursor: rowTapToComplete ? 'pointer' : 'default',
          touchAction: 'manipulation',
          userSelect: 'none',
          '&:active': rowTapToComplete ? { bgcolor: 'action.hover' } : undefined,
        }}
      >
        {/* Leading control: checkbox for checkbox items, completion dot otherwise */}
        {item.type === 'checkbox' ? (
          <Checkbox
            checked={isChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={handleToggleChecked}
            disabled={disabled}
            inputProps={{ 'aria-label': item.label }}
            sx={{ p: 1, minWidth: MIN_TOUCH_TARGET, minHeight: MIN_TOUCH_TARGET }}
          />
        ) : (
          <CheckCircleIcon
            sx={{
              mt: 1.25,
              ml: 0.5,
              fontSize: 22,
              color: isChecked ? 'success.main' : 'action.disabled',
            }}
          />
        )}

        {/* Label + inline value editors. flex + minWidth:0 lets long labels wrap. */}
        <Box sx={{ flex: 1, minWidth: 0, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              fontWeight={isChecked ? 400 : 500}
              sx={{ overflowWrap: 'anywhere', minWidth: 0 }}
            >
              {item.label}
            </Typography>
            {item.required && !isChecked && (
              <Chip
                label={t('booking.requiredItem')}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {photoRequired && (
              <Chip
                icon={<PhotoCameraIcon sx={{ fontSize: '0.9rem' }} />}
                label={t('booking.photoRequired')}
                size="small"
                color={photoMissing ? 'warning' : 'success'}
                variant={photoMissing ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {/* Inline text input */}
          {item.type === 'text' && (
            <TextField
              value={entry?.value ?? ''}
              onChange={(e) => handleValueChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={item.config?.placeholder}
              size="small"
              fullWidth
              multiline
              maxRows={3}
              disabled={disabled}
              sx={{ mt: 0.5 }}
            />
          )}

          {/* Inline numeric input with unit display */}
          {item.type === 'numeric' && (
            <TextField
              value={entry?.value ?? ''}
              onChange={(e) =>
                handleValueChange(e.target.value ? Number(e.target.value) : '')
              }
              onClick={(e) => e.stopPropagation()}
              type="number"
              size="small"
              disabled={disabled}
              sx={{ mt: 0.5, maxWidth: 200 }}
              slotProps={{
                input: item.config?.unit
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          {item.config.unit}
                        </InputAdornment>
                      ),
                    }
                  : undefined,
                htmlInput: {
                  inputMode: 'decimal',
                  min: item.config?.min,
                  max: item.config?.max,
                },
              }}
            />
          )}
        </Box>

        {/* Trailing quick actions */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <Tooltip title={t('mechanic.addNote')}>
            <span>
              <IconButton
                aria-label={t('mechanic.addNote')}
                onClick={() => setNoteOpen((v) => !v)}
                disabled={disabled}
                color={hasNote ? 'primary' : 'default'}
                sx={{ width: MIN_TOUCH_TARGET, height: MIN_TOUCH_TARGET }}
              >
                <NotesIcon />
              </IconButton>
            </span>
          </Tooltip>
          {canAttachPhoto && item.type !== 'photo' && (
            <Tooltip title={t('mechanic.addPhoto')}>
              <span>
                <IconButton
                  aria-label={t('mechanic.addPhoto')}
                  onClick={() => setPhotoOpen((v) => !v)}
                  disabled={disabled || isUploadingPhoto}
                  color={itemPhotos.length > 0 ? 'primary' : photoMissing ? 'warning' : 'default'}
                  sx={{ width: MIN_TOUCH_TARGET, height: MIN_TOUCH_TARGET }}
                >
                  <PhotoCameraIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Inline note editor */}
      <Collapse in={noteOpen} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <TextField
            inputRef={noteInputRef}
            value={entry?.notes ?? ''}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder={t('mechanic.notePlaceholder')}
            size="small"
            fullWidth
            multiline
            maxRows={4}
            disabled={disabled}
          />
        </Box>
      </Collapse>

      {/* Photo capture (direct camera/gallery via existing flow) */}
      {canAttachPhoto && (
        <Collapse in={photoOpen || item.type === 'photo'} unmountOnExit>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <PhotoCapture
              photos={itemPhotos.map((p) => ({
                id: p.id,
                url: p.fileUrl,
                thumbnailUrl: p.thumbnailUrl ?? undefined,
                caption: p.caption ?? undefined,
              }))}
              onCapture={
                !disabled && onAddPhoto ? (file) => onAddPhoto(item.id, file) : undefined
              }
              onDelete={
                !disabled && onDeletePhoto
                  ? (photoId) => onDeletePhoto(item.id, photoId)
                  : undefined
              }
              disabled={disabled || isUploadingPhoto}
              maxPhotos={item.photoConfig?.maxPhotos ?? 5}
              itemName={item.label}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
